import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import {
  ClipboardCheck, Plus, Search, Filter, Calendar, AlertTriangle,
  CheckCircle2, XCircle, AlertCircle, Eye, ChevronRight, RefreshCw,
  Users, TrendingUp, FileWarning, Clock, Award, Target, BarChart3
} from "lucide-react";
import { toast } from "sonner";

// Compliance level colors
const COMPLIANCE_COLORS = {
  COMPLIANT: { bg: "bg-green-100", text: "text-green-700", border: "border-green-500" },
  OFI: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-500" },
  MINOR_NC: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-500" },
  MAJOR_NC: { bg: "bg-red-100", text: "text-red-700", border: "border-red-500" }
};

const COMPLIANCE_LABELS = {
  COMPLIANT: "Compliant",
  OFI: "Opportunity for Improvement",
  MINOR_NC: "Minor Non-Conformance",
  MAJOR_NC: "Major Non-Conformance"
};

export default function AuditPage() {
  const { user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showConduct, setShowConduct] = useState(null);  // NEW: Conduct Audit modal
  const [filters, setFilters] = useState({ status: "", circle_name: "" });
  const [circles, setCircles] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.circle_name) params.circle_name = filters.circle_name;

      const [auditsRes, statsRes, circlesRes] = await Promise.all([
        api.get("/audit", { params }),
        api.get("/audit/stats"),
        api.get("/auth/circles")
      ]);

      setAudits(auditsRes.data.items || []);
      setStats(statsRes.data);
      setCircles(circlesRes.data || []);
    } catch (e) {
      toast.error("Failed to load audit data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filters]);

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: "bg-slate-100 text-slate-600",
      IN_PROGRESS: "bg-blue-100 text-blue-600",
      SUBMITTED: "bg-purple-100 text-purple-600",
      CLOSED: "bg-green-100 text-green-600"
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-bold ${styles[status] || "bg-slate-100"}`}>
        {status}
      </span>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    if (score >= 40) return "text-orange-600";
    return "text-red-600";
  };

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="audit-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            Digital Audit & Compliance
          </h1>
          <p className="text-slate-500 text-sm">ISO-Standard Weighted Non-Conformance Model</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
          </button>
          {["admin", "package_head"].includes(user?.role) && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold text-sm hover:bg-orange-600"
              data-testid="create-audit-btn"
            >
              <Plus size={16} />New Audit
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Total Audits</div>
            <div className="text-2xl font-black text-slate-800">{stats.total_audits}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Closed</div>
            <div className="text-2xl font-black text-green-600">{stats.closed_audits}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs text-slate-500 uppercase font-bold">In Progress</div>
            <div className="text-2xl font-black text-blue-600">{stats.in_progress}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Avg Score</div>
            <div className={`text-2xl font-black ${getScoreColor(stats.average_score)}`}>
              {stats.average_score}%
            </div>
          </div>
          <div className="bg-white border border-red-200 rounded-sm p-3">
            <div className="text-xs text-red-500 uppercase font-bold">Urgent CAPs</div>
            <div className="text-2xl font-black text-red-600">{stats.urgent_caps}</div>
          </div>
          <div className="bg-white border border-yellow-200 rounded-sm p-3">
            <div className="text-xs text-yellow-600 uppercase font-bold">Open CAPs</div>
            <div className="text-2xl font-black text-yellow-600">{stats.open_caps}</div>
          </div>
          <div className="bg-white border border-purple-200 rounded-sm p-3">
            <div className="text-xs text-purple-500 uppercase font-bold">Under Review</div>
            <div className="text-2xl font-black text-purple-600">{stats.under_review_caps}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <div className="text-xs text-slate-500 uppercase font-bold">Critical Fails</div>
            <div className="text-2xl font-black text-red-600">{stats.critical_failures}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-3">
        <select
          className="field-select h-9 text-xs"
          value={filters.status}
          onChange={e => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="CLOSED">Closed</option>
        </select>
        {["admin", "package_head"].includes(user?.role) && (
          <select
            className="field-select h-9 text-xs"
            value={filters.circle_name}
            onChange={e => setFilters({ ...filters, circle_name: e.target.value })}
          >
            <option value="">All Circles</option>
            {circles.map(c => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Audits List */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
            <p className="text-slate-500">Loading audits...</p>
          </div>
        ) : audits.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <ClipboardCheck size={32} className="mx-auto mb-2" />
            <p>No audits found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {audits.map(audit => (
              <div
                key={audit.id}
                className="p-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-sm bg-orange-100 flex items-center justify-center">
                      <ClipboardCheck className="text-orange-600" size={24} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-slate-800">{audit.circle_name}</h3>
                        {getStatusBadge(audit.status)}
                        {audit.critical_fail_triggered && (
                          <span className="px-2 py-0.5 bg-red-600 text-white rounded text-xs font-bold">
                            CRITICAL FAIL
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">
                        CI: {audit.ci_name || "N/A"} • {new Date(audit.audit_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {audit.status === "CLOSED" && (
                      <div className="text-right">
                        <div className={`text-2xl font-black ${getScoreColor(audit.final_percentage)}`}>
                          {audit.final_percentage}%
                        </div>
                        <div className="text-xs text-slate-500">
                          {audit.total_points_scored}/{audit.max_possible_points} pts
                        </div>
                      </div>
                    )}
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      {["DRAFT", "IN_PROGRESS"].includes(audit.status) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowConduct(audit.id); }}
                          className="px-3 py-2 bg-green-500 text-white rounded-sm font-bold text-xs hover:bg-green-600 flex items-center gap-1"
                          data-testid={`conduct-audit-${audit.id}`}
                        >
                          <ClipboardCheck size={14} />
                          Conduct Audit
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowDetail(audit.id); }}
                        className="px-3 py-2 bg-slate-100 text-slate-700 rounded-sm font-bold text-xs hover:bg-slate-200 flex items-center gap-1"
                      >
                        <Eye size={14} />
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Audit Modal */}
      {showCreate && (
        <CreateAuditModal
          circles={circles}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchData(); }}
        />
      )}

      {/* Conduct Audit Modal */}
      {showConduct && (
        <ConductAuditModal
          auditId={showConduct}
          onClose={() => setShowConduct(null)}
          onUpdate={() => { setShowConduct(null); fetchData(); }}
        />
      )}

      {/* Audit Detail Modal */}
      {showDetail && (
        <AuditDetailModal
          auditId={showDetail}
          onClose={() => setShowDetail(null)}
          onUpdate={fetchData}
        />
      )}
    </div>
  );
}

// =============================================================================
// CONDUCT AUDIT MODAL — Full Questionnaire with live scoring
// =============================================================================
function ConductAuditModal({ auditId, onClose, onUpdate }) {
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [categoriesData, setCategoriesData] = useState([]);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(0);
  const [closing, setClosing] = useState(false);

  useEffect(() => { loadAuditAndQuestions(); }, [auditId]);

  const loadAuditAndQuestions = async () => {
    try {
      const [auditRes, questionsRes] = await Promise.all([
        api.get(`/audit/${auditId}`),
        api.get("/audit/questions")
      ]);
      setAudit(auditRes.data);

      // Group questions by category
      const questions = questionsRes.data.items || [];
      const catMap = {};
      questions.forEach(q => {
        const catName = q.category_name || "Other";
        if (!catMap[catName]) catMap[catName] = { name: catName, category_id: q.category_id, questions: [] };
        catMap[catName].questions.push(q);
      });
      const cats = Object.values(catMap);
      setCategoriesData(cats);

      // Pre-fill existing responses
      const existing = {};
      (auditRes.data.responses || []).forEach(r => {
        existing[r.question_id] = {
          compliance_level: r.compliance_level,
          remarks: r.remarks || "",
          response_id: r.id,
          points_awarded: r.points_awarded,
          has_evidence: r.evidence?.length > 0
        };
      });
      setAnswers(existing);
    } catch (e) {
      toast.error("Failed to load audit data");
    } finally {
      setLoading(false);
    }
  };

  const submitResponse = async (questionId, compliance_level, remarks) => {
    setSaving(s => ({ ...s, [questionId]: true }));
    try {
      const res = await api.post(`/audit/${auditId}/responses`, {
        question_id: questionId,
        compliance_level,
        remarks: remarks || ""
      });
      setAnswers(prev => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          compliance_level,
          remarks: remarks || "",
          response_id: res.data.response_id,
          points_awarded: res.data.points_awarded
        }
      }));
      toast.success("Response saved", { duration: 1500 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save response");
    } finally {
      setSaving(s => ({ ...s, [questionId]: false }));
    }
  };

  const handleComplianceSelect = (questionId, level) => {
    const currentRemarks = answers[questionId]?.remarks || "";
    submitResponse(questionId, level, currentRemarks);
  };

  const handleRemarksBlur = (questionId) => {
    const ans = answers[questionId];
    if (ans?.compliance_level) {
      submitResponse(questionId, ans.compliance_level, ans.remarks);
    }
  };

  const handleCalculateAndClose = async () => {
    const allQuestions = categoriesData.flatMap(c => c.questions);
    const unanswered = allQuestions.filter(q => !answers[q.id]?.compliance_level);
    if (unanswered.length > 0) {
      toast.error(`${unanswered.length} questions still unanswered`);
      return;
    }
    if (!confirm("Calculate final score and close this audit? This will auto-generate CAPs for non-conformances.")) return;
    setClosing(true);
    try {
      const res = await api.post(`/audit/${auditId}/calculate-and-close`);
      toast.success(`Audit closed! Score: ${res.data.final_percentage}%, ${res.data.caps_generated} CAPs generated`);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to close audit");
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
        <div className="bg-white p-8 rounded-sm">
          <RefreshCw className="animate-spin text-orange-500 mx-auto" size={32} />
        </div>
      </div>
    );
  }

  const allQuestions = categoriesData.flatMap(c => c.questions);
  const answeredCount = allQuestions.filter(q => answers[q.id]?.compliance_level).length;
  const totalCount = allQuestions.length;
  const progressPct = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;
  const currentCat = categoriesData[activeCategory] || { questions: [] };

  const complianceBtns = [
    { level: "COMPLIANT", label: "Compliant", color: "bg-green-500 hover:bg-green-600", ring: "ring-green-300" },
    { level: "OFI", label: "OFI", color: "bg-blue-500 hover:bg-blue-600", ring: "ring-blue-300" },
    { level: "MINOR_NC", label: "Minor NC", color: "bg-yellow-500 hover:bg-yellow-600", ring: "ring-yellow-300" },
    { level: "MAJOR_NC", label: "Major NC", color: "bg-red-500 hover:bg-red-600", ring: "ring-red-300" }
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-2 md:p-4" data-testid="conduct-audit-modal">
      <div className="bg-white rounded-sm w-full max-w-4xl shadow-xl flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="border-b border-slate-200 px-4 py-3 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-bold text-slate-900" data-testid="conduct-audit-title">Conduct Audit</h3>
            <p className="text-xs text-slate-500">{audit?.circle_name} - {new Date(audit?.audit_date).toLocaleDateString()}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className="text-sm font-bold text-slate-700" data-testid="audit-progress">{answeredCount}/{totalCount}</span>
              <div className="w-24 h-1.5 bg-slate-200 rounded-full mt-0.5">
                <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded text-slate-500 text-lg font-bold" data-testid="close-conduct-modal">X</button>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex border-b border-slate-200 shrink-0 overflow-x-auto">
          {categoriesData.map((cat, idx) => {
            const catAnswered = cat.questions.filter(q => answers[q.id]?.compliance_level).length;
            return (
              <button
                key={cat.name}
                onClick={() => setActiveCategory(idx)}
                className={`px-4 py-2.5 text-sm font-bold whitespace-nowrap transition-colors relative ${
                  activeCategory === idx ? "text-orange-600 border-b-2 border-orange-500" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`cat-tab-${cat.name.toLowerCase()}`}
              >
                {cat.name}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  catAnswered === cat.questions.length ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                }`}>{catAnswered}/{cat.questions.length}</span>
              </button>
            );
          })}
        </div>

        {/* Questions List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="questions-container">
          {currentCat.questions.map((q, qIdx) => {
            const ans = answers[q.id] || {};
            const isSaving = saving[q.id];
            return (
              <div
                key={q.id}
                className={`border rounded-sm p-4 transition-colors ${
                  ans.compliance_level
                    ? ans.compliance_level === "COMPLIANT" ? "border-green-200 bg-green-50/30"
                    : ans.compliance_level === "OFI" ? "border-blue-200 bg-blue-50/30"
                    : ans.compliance_level === "MINOR_NC" ? "border-yellow-200 bg-yellow-50/30"
                    : "border-red-200 bg-red-50/30"
                    : "border-slate-200 bg-white"
                }`}
                data-testid={`question-card-${qIdx}`}
              >
                {/* Question header */}
                <div className="flex items-start gap-3 mb-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                    {qIdx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800 leading-snug">{q.question_text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-bold text-slate-500">Weight: {q.weightage}</span>
                      {q.is_critical_fail && (
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-bold flex items-center gap-0.5">
                          <AlertTriangle size={10} /> CRITICAL
                        </span>
                      )}
                      {isSaving && <RefreshCw size={12} className="animate-spin text-orange-500" />}
                      {ans.compliance_level && !isSaving && <CheckCircle2 size={12} className="text-green-500" />}
                    </div>
                  </div>
                  {ans.points_awarded !== undefined && (
                    <span className="text-xs font-bold text-slate-600 shrink-0">{ans.points_awarded}/{q.weightage} pts</span>
                  )}
                </div>

                {/* Compliance level buttons */}
                <div className="flex flex-wrap gap-2 mb-2">
                  {complianceBtns.map(btn => (
                    <button
                      key={btn.level}
                      onClick={() => handleComplianceSelect(q.id, btn.level)}
                      disabled={isSaving}
                      className={`px-3 py-1.5 rounded-sm text-xs font-bold text-white transition-all ${btn.color} ${
                        ans.compliance_level === btn.level ? `ring-2 ${btn.ring} scale-105` : "opacity-60 hover:opacity-100"
                      } disabled:opacity-40`}
                      data-testid={`compliance-${btn.level.toLowerCase()}-${qIdx}`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>

                {/* Remarks input */}
                <input
                  type="text"
                  placeholder="Add remarks (optional)..."
                  className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-sm bg-white/80"
                  value={ans.remarks || ""}
                  onChange={e => setAnswers(prev => ({ ...prev, [q.id]: { ...prev[q.id], remarks: e.target.value } }))}
                  onBlur={() => handleRemarksBlur(q.id)}
                  data-testid={`remarks-${qIdx}`}
                />
              </div>
            );
          })}
        </div>

        {/* Footer with actions */}
        <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between shrink-0 bg-slate-50">
          <div className="text-xs text-slate-500">
            {answeredCount === totalCount ? (
              <span className="text-green-600 font-bold flex items-center gap-1">
                <CheckCircle2 size={14} /> All questions answered
              </span>
            ) : (
              <span>{totalCount - answeredCount} questions remaining</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-sm text-sm font-bold text-slate-600 hover:bg-slate-100" data-testid="cancel-conduct-btn">
              Save & Exit
            </button>
            {answeredCount === totalCount && (
              <button
                onClick={handleCalculateAndClose}
                disabled={closing}
                className="px-4 py-2 bg-green-500 text-white rounded-sm font-bold text-sm hover:bg-green-600 disabled:opacity-60 flex items-center gap-1.5"
                data-testid="calculate-close-btn"
              >
                <Target size={14} />
                {closing ? "Processing..." : "Calculate & Close"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Create Audit Modal
function CreateAuditModal({ circles, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [ciUsers, setCiUsers] = useState([]);
  const [form, setForm] = useState({
    circle_name: "",
    ci_user_id: "",
    scheduled_date: new Date().toISOString().slice(0, 10),
    notes: ""
  });

  // Fetch CI users when circle changes
  useEffect(() => {
    if (form.circle_name) {
      api.get("/employees", { params: { circle_name: form.circle_name, role: "ci" } })
        .then(res => {
          // For demo, we'll use the users endpoint
          api.get("/auth/me").then(() => {
            // Just set empty for now - in real app, fetch CI users
            setCiUsers([]);
          });
        })
        .catch(() => setCiUsers([]));
    }
  }, [form.circle_name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.circle_name || !form.ci_user_id) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/audit", form);
      toast.success("Audit created successfully");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create audit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-sm w-full max-w-md shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-bold">Create New Audit</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="field-label">Circle *</label>
            <select
              className="field-select"
              required
              value={form.circle_name}
              onChange={e => setForm({ ...form, circle_name: e.target.value, ci_user_id: "" })}
            >
              <option value="">Select Circle</option>
              {circles.map(c => (
                <option key={c.name} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label">Circle Incharge (CI) User ID *</label>
            <input
              className="field-input"
              required
              placeholder="Enter CI User ID"
              value={form.ci_user_id}
              onChange={e => setForm({ ...form, ci_user_id: e.target.value })}
            />
            <p className="text-xs text-slate-500 mt-1">Get CI User ID from Employees page</p>
          </div>
          <div>
            <label className="field-label">Scheduled Date</label>
            <input
              type="date"
              className="field-input"
              value={form.scheduled_date}
              onChange={e => setForm({ ...form, scheduled_date: e.target.value })}
            />
          </div>
          <div>
            <label className="field-label">Notes</label>
            <textarea
              className="field-input"
              rows={2}
              placeholder="Optional notes..."
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold disabled:opacity-60"
            >
              {loading ? "Creating..." : "Create Audit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Audit Detail Modal (Full Dossier View)
function AuditDetailModal({ auditId, onClose, onUpdate }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    fetchAudit();
  }, [auditId]);

  const fetchAudit = async () => {
    try {
      const res = await api.get(`/audit/${auditId}`);
      setAudit(res.data);
    } catch (e) {
      toast.error("Failed to load audit details");
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateAndClose = async () => {
    if (!confirm("Calculate final score and close this audit? This will auto-generate CAPs for non-conformances.")) return;
    setClosing(true);
    try {
      const res = await api.post(`/audit/${auditId}/calculate-and-close`);
      toast.success(`Audit closed! Score: ${res.data.final_percentage}%, ${res.data.caps_generated} CAPs generated`);
      fetchAudit();
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to close audit");
    } finally {
      setClosing(false);
    }
  };

  const getComplianceIcon = (level) => {
    switch (level) {
      case "COMPLIANT": return <CheckCircle2 className="text-green-500" size={18} />;
      case "OFI": return <AlertCircle className="text-blue-500" size={18} />;
      case "MINOR_NC": return <AlertTriangle className="text-yellow-500" size={18} />;
      case "MAJOR_NC": return <XCircle className="text-red-500" size={18} />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
        <div className="bg-white p-8 rounded-sm">
          <RefreshCw className="animate-spin text-orange-500 mx-auto" size={32} />
        </div>
      </div>
    );
  }

  if (!audit) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-sm w-full max-w-4xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold">{audit.circle_name} Audit</h3>
              <p className="text-sm text-slate-500">
                CI: {audit.ci_user?.full_name} • {new Date(audit.audit_date).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {audit.status !== "CLOSED" && (
                <button
                  onClick={handleCalculateAndClose}
                  disabled={closing}
                  className="px-4 py-2 bg-green-500 text-white rounded-sm font-bold text-sm hover:bg-green-600 disabled:opacity-60"
                >
                  {closing ? "Processing..." : "Calculate & Close"}
                </button>
              )}
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded">×</button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Score Summary (if closed) */}
          {audit.status === "CLOSED" && (
            <div className={`mb-6 p-6 rounded-sm ${audit.critical_fail_triggered ? "bg-red-50 border-2 border-red-500" : "bg-green-50 border border-green-200"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-500 uppercase">Final Score</div>
                  <div className={`text-5xl font-black ${audit.critical_fail_triggered ? "text-red-600" : "text-green-600"}`}>
                    {audit.final_percentage}%
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    {audit.total_points_scored} / {audit.max_possible_points} points
                  </div>
                </div>
                {audit.critical_fail_triggered && (
                  <div className="text-right">
                    <div className="px-4 py-2 bg-red-600 text-white rounded-sm font-bold">
                      CRITICAL FAILURE
                    </div>
                    <p className="text-xs text-red-600 mt-2">Auto-zeroed due to critical non-conformance</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Category Breakdown */}
          {audit.category_breakdown && Object.keys(audit.category_breakdown).length > 0 && (
            <div className="mb-6">
              <h4 className="font-bold text-slate-800 mb-3">Category Breakdown</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(audit.category_breakdown).map(([catName, catData]) => (
                  <div key={catName} className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                    <div className="font-bold text-slate-700">{catName}</div>
                    <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                      <div className="text-center">
                        <div className="text-green-600 font-bold">{catData.stats.compliant}</div>
                        <div className="text-slate-500">Pass</div>
                      </div>
                      <div className="text-center">
                        <div className="text-blue-600 font-bold">{catData.stats.ofi}</div>
                        <div className="text-slate-500">OFI</div>
                      </div>
                      <div className="text-center">
                        <div className="text-yellow-600 font-bold">{catData.stats.minor_nc}</div>
                        <div className="text-slate-500">Minor</div>
                      </div>
                      <div className="text-center">
                        <div className="text-red-600 font-bold">{catData.stats.major_nc}</div>
                        <div className="text-slate-500">Major</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Responses Detail Grid */}
          <div className="mb-6">
            <h4 className="font-bold text-slate-800 mb-3">Audit Responses ({audit.responses?.length || 0})</h4>
            <div className="border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 font-bold">Question</th>
                    <th className="text-center p-3 font-bold w-20">Weight</th>
                    <th className="text-center p-3 font-bold w-32">Compliance</th>
                    <th className="text-center p-3 font-bold w-20">Points</th>
                    <th className="text-left p-3 font-bold w-40">Remarks</th>
                    <th className="text-center p-3 font-bold w-20">Evidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(audit.responses || []).map(resp => (
                    <tr key={resp.id} className="hover:bg-slate-50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {resp.question?.is_critical_fail && (
                            <span className="px-1 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-bold">CRITICAL</span>
                          )}
                          {resp.question?.question_text || "N/A"}
                        </div>
                      </td>
                      <td className="p-3 text-center font-bold">{resp.question?.weightage || 0}</td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${COMPLIANCE_COLORS[resp.compliance_level]?.bg} ${COMPLIANCE_COLORS[resp.compliance_level]?.text}`}>
                          {getComplianceIcon(resp.compliance_level)}
                          <span className="text-xs font-bold">{resp.compliance_level}</span>
                        </span>
                      </td>
                      <td className="p-3 text-center font-bold">{resp.points_awarded}</td>
                      <td className="p-3 text-xs text-slate-600 truncate max-w-[150px]" title={resp.remarks}>
                        {resp.remarks || "-"}
                      </td>
                      <td className="p-3 text-center">
                        {resp.evidence?.length > 0 ? (
                          <a href={resp.evidence[0].image_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            View
                          </a>
                        ) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CAPs Section */}
          {audit.caps && audit.caps.length > 0 && (
            <div>
              <h4 className="font-bold text-slate-800 mb-3">Corrective Action Plans ({audit.caps.length})</h4>
              <div className="space-y-2">
                {audit.caps.map(cap => (
                  <div key={cap.id} className={`p-4 border rounded-sm ${cap.severity === "URGENT" ? "border-red-300 bg-red-50" : "border-yellow-300 bg-yellow-50"}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${cap.severity === "URGENT" ? "bg-red-600 text-white" : "bg-yellow-600 text-white"}`}>
                          {cap.severity}
                        </span>
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${cap.status === "RESOLVED" ? "bg-green-100 text-green-700" : cap.status === "UNDER_REVIEW" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-700"}`}>
                          {cap.status}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500">
                        Deadline: {new Date(cap.deadline).toLocaleString()}
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 mt-2">{cap.question_text}</p>
                    <p className="text-xs text-slate-500 mt-1">Assigned to: {cap.assigned_to_name || cap.assigned_to_user_id}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
